"use client"

import * as React from "react"

import { cn } from "@ibada/ui"

import { useLocale } from "@/i18n"

/** Each wheel row, in px: the snap math and the centre band both use it. */
const ITEM_HEIGHT = 44
/** Rows visible at once (odd, so one sits in the centre band). */
const VISIBLE_ROWS = 5

export type MobileWheelItem = { value: number; label: string }

/**
 * One scrolling column of a phone wheel: flick it and it snaps to the row in the centre band, or tap a row to
 * bring it there. Lives inside a bottom sheet, so it opts out of the sheet's drag.
 */
export function MobileWheelColumn({
  items,
  value,
  onChange,
  ariaLabel,
  className,
}: {
  items: MobileWheelItem[]
  value: number
  onChange: (value: number) => void
  ariaLabel: string
  className?: string
}) {
  const ref = React.useRef<HTMLDivElement>(null)
  const settleRef = React.useRef<number | null>(null)
  const index = Math.max(
    0,
    items.findIndex((item) => item.value === value),
  )

  // Follow the value when it changes from outside (opening, the period column), without fighting a flick.
  React.useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    const target = index * ITEM_HEIGHT
    if (Math.abs(node.scrollTop - target) > 1) node.scrollTop = target
  }, [index])

  React.useEffect(
    () => () => {
      if (settleRef.current) window.clearTimeout(settleRef.current)
    },
    [],
  )

  const settle = () => {
    const node = ref.current
    if (!node) return
    const next = items[Math.min(items.length - 1, Math.max(0, Math.round(node.scrollTop / ITEM_HEIGHT)))]
    if (next && next.value !== value) onChange(next.value)
  }

  return (
    <div
      ref={ref}
      role='listbox'
      aria-label={ariaLabel}
      data-vaul-no-drag
      onScroll={() => {
        if (settleRef.current) window.clearTimeout(settleRef.current)
        settleRef.current = window.setTimeout(settle, 90)
      }}
      className={cn("relative z-10 snap-y snap-mandatory overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden", className)}
      style={{
        height: ITEM_HEIGHT * VISIBLE_ROWS,
        paddingBlock: ITEM_HEIGHT * Math.floor(VISIBLE_ROWS / 2),
        maskImage: "linear-gradient(to bottom, transparent, black 30%, black 70%, transparent)",
        WebkitMaskImage: "linear-gradient(to bottom, transparent, black 30%, black 70%, transparent)",
      }}
    >
      {items.map((item) => {
        const selected = item.value === value
        return (
          <button
            key={item.value}
            type='button'
            role='option'
            aria-selected={selected}
            onClick={() => ref.current?.scrollTo({ top: items.indexOf(item) * ITEM_HEIGHT, behavior: "smooth" })}
            style={{ height: ITEM_HEIGHT }}
            className={cn(
              "flex w-full snap-center items-center justify-center text-[1.375rem] tabular-nums transition-colors",
              selected ? "font-extrabold text-foreground" : "font-semibold text-muted-foreground/70",
            )}
          >
            {item.label}
          </button>
        )
      })}
    </div>
  )
}

const pad2 = (n: number) => String(n).padStart(2, "0")

/** Whether the locale writes times on a 12-hour clock (en) or 24-hour (fr). */
function uses12Hour(locale: string) {
  try {
    return new Intl.DateTimeFormat(locale, { hour: "numeric" }).resolvedOptions().hour12 ?? false
  } catch {
    return false
  }
}

function periodLabel(locale: string, hour: number, fallback: string) {
  try {
    return (
      new Intl.DateTimeFormat(locale, { hour: "numeric", hour12: true })
        .formatToParts(new Date(2000, 0, 1, hour))
        .find((part) => part.type === "dayPeriod")?.value ?? fallback
    )
  } catch {
    return fallback
  }
}

const MINUTES: MobileWheelItem[] = Array.from({ length: 60 }, (_, minute) => ({ value: minute, label: pad2(minute) }))
const HOURS_24: MobileWheelItem[] = Array.from({ length: 24 }, (_, hour) => ({ value: hour, label: pad2(hour) }))
const HOURS_12: MobileWheelItem[] = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: String(i + 1) }))

/**
 * The phone time picker: hour and minute wheels (plus AM/PM where the locale uses a 12-hour clock) in the app's
 * style, never the browser's own time popup. Takes and gives a 24-hour "HH:mm" string.
 */
export function MobileTimeWheel({
  value,
  onChange,
  hourLabel,
  minuteLabel,
}: {
  value: string
  onChange: (value: string) => void
  hourLabel: string
  minuteLabel: string
}) {
  const locale = useLocale()
  const twelve = uses12Hour(locale)
  const [rawHour = "0", rawMinute = "0"] = value.split(":")
  const hour = Math.min(23, Math.max(0, Number(rawHour) || 0))
  const minute = Math.min(59, Math.max(0, Number(rawMinute) || 0))
  const emit = (nextHour: number, nextMinute: number) => onChange(`${pad2(nextHour)}:${pad2(nextMinute)}`)

  const isPm = hour >= 12
  const hour12 = hour % 12 === 0 ? 12 : hour % 12
  const periods: MobileWheelItem[] = [
    { value: 0, label: periodLabel(locale, 6, "AM") },
    { value: 1, label: periodLabel(locale, 18, "PM") },
  ]

  return (
    <div className='relative mx-auto flex w-full max-w-xs items-center justify-center gap-1'>
      <div
        aria-hidden
        className='pointer-events-none absolute inset-x-0 top-1/2 z-0 -translate-y-1/2 rounded-xl bg-muted'
        style={{ height: ITEM_HEIGHT }}
      />
      <MobileWheelColumn
        ariaLabel={hourLabel}
        className='w-20'
        items={twelve ? HOURS_12 : HOURS_24}
        value={twelve ? hour12 : hour}
        onChange={(next) => emit(twelve ? (next % 12) + (isPm ? 12 : 0) : next, minute)}
      />
      <span aria-hidden className='relative z-10 text-[1.375rem] font-extrabold'>
        :
      </span>
      <MobileWheelColumn ariaLabel={minuteLabel} className='w-20' items={MINUTES} value={minute} onChange={(next) => emit(hour, next)} />
      {twelve ? (
        <MobileWheelColumn
          ariaLabel={hourLabel}
          className='w-20'
          items={periods}
          value={isPm ? 1 : 0}
          onChange={(next) => emit((hour % 12) + (next === 1 ? 12 : 0), minute)}
        />
      ) : null}
    </div>
  )
}
